import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import { getInvoices } from "../../../../services/Admin/InvoicesApi";
import InvoiceDetailsDialog from "./InvoiceDetailsDialog";
import EditeInvoiceData from "./EditeInvoiceData";
import DeleteInvoiceDialog from "./DeleteInvoiceDialog";
import ExportInvoicesExcelButton from "./ExportInvoicesExcelButton";
import { handleBrowserPrintInvoicesReport } from "./PrintInvoices/printUtilsInvoices";
import { useAuth } from "../../../../Context/AuthContext";
import { formatDateTime12Hour } from "../../../../utils/dateTimeFormat";

const getTodayDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeTimeDigits = (value) =>
  String(value || "")
    .replace(/[٠-٩]/g, (digit) => "٠١٢٣٤٥٦٧٨٩".indexOf(digit))
    .replace(/[۰-۹]/g, (digit) => "۰۱۲۳۴۵۶۷۸۹".indexOf(digit));

const parseTimeInput = (value, fallback) => {
  const normalizedValue = normalizeTimeDigits(value).trim();
  const match = normalizedValue.match(/^(\d{1,2}):(\d{2})$/);

  if (!match) return fallback;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return fallback;

  return { hours, minutes };
};

const buildFilterDateTime = (dateValue, timeValue, fallbackTime) => {
  if (!dateValue) return null;

  const [year, month, day] = dateValue.split("-").map(Number);
  if (!year || !month || !day) return null;

  const parsedTime = parseTimeInput(timeValue, fallbackTime);
  const date = new Date(year, month - 1, day, parsedTime.hours, parsedTime.minutes, 0, 0);

  return Number.isNaN(date.getTime()) ? null : date;
};


const InvoicesPage = () => {
  const today = getTodayDateString();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [timeFrom, setTimeFrom] = useState("00:00");
  const [timeTo, setTimeTo] = useState("23:59");
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  const [userNameFilter, setUserNameFilter] = useState("all");
  const { hasPermission } = useAuth();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getInvoices();
      setInvoices(response.data || []);
    } catch (err) {
      console.error("خطأ في جلب الفواتير:", err);
      setError(err.message || "حدث خطأ في جلب الفواتير");
    } finally {
      setLoading(false);
    }
  };

  const getOrderTypeLabel = (type) => {
    const types = {
      tables: "طاولات",
      delivery: "توصيل",
      takeaway: "استلام",
    };
    return types[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "warning",
      finished: "success",
      canceled: "error",
      underway: "info",
      out_of_preparation: "primary",
      delivered: "success",
      under_review: "default",
    };
    return colors[status] || "default";
  };

  const handleRowClick = (invoiceId) => {
  if (hasPermission("view")) { // غير الاسم حسب الباك عندك
    setSelectedInvoiceId(invoiceId);
    setOpenDetailsDialog(true);
  } else {
    alert("ليس لديك الصلاحية لعرض تفاصيل الفاتورة");
  }
};

  const handleEditClick = (e, invoice) => {
    e.stopPropagation(); // منع فتح ديالوج التفاصيل
    setSelectedInvoice(invoice);
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (e, invoice) => {
    e.stopPropagation(); // منع فتح ديالوج التفاصيل
    setInvoiceToDelete(invoice);
    setOpenDeleteDialog(true);
  };

  const handlePrintFilteredInvoices = () => {
    const sanitizedInvoices = filteredInvoices.map((invoice) => {
      const sanitizedOrder = invoice?.order
        ? {
            ...invoice.order,
            order_items: [],
            items: [],
          }
        : invoice?.order;

      return {
        ...invoice,
        order: sanitizedOrder,
        order_items: [],
        items: [],
      };
    });

    handleBrowserPrintInvoicesReport(sanitizedInvoices);
  };

  const userNameOptions = useMemo(() => {
    const names = invoices
      .map((invoice) => invoice?.user?.name)
      .filter((name) => Boolean(name && String(name).trim()));

    return Array.from(new Set(names));
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    const fromDate = buildFilterDateTime(dateFrom, timeFrom, {
      hours: 0,
      minutes: 0,
    });
    const toDateEnd = buildFilterDateTime(dateTo, timeTo, {
      hours: 23,
      minutes: 59,
    });

    if (toDateEnd) {
      toDateEnd.setSeconds(59, 999);
    }

    return invoices.filter((invoice) => {
      const matchesOrderType =
        orderTypeFilter === "all" || invoice?.order?.order_type === orderTypeFilter;
      if (!matchesOrderType) return false;

      const matchesUserName =
        userNameFilter === "all" || invoice?.user?.name === userNameFilter;
      if (!matchesUserName) return false;

      const invoiceDateRaw = invoice?.created_at;
      const invoiceDate = invoiceDateRaw ? new Date(invoiceDateRaw) : null;

      if (fromDate || toDateEnd) {
        if (!invoiceDate || Number.isNaN(invoiceDate.getTime())) return false;
        if (fromDate && invoiceDate < fromDate) return false;
        if (toDateEnd && invoiceDate > toDateEnd) return false;
      }

      if (!q) return true;

      const haystack = [
        invoice?.id,
        invoice?.order?.id,
        invoice?.order?.general_increment,
        invoice?.order?.daily_increment,
        invoice?.order?.monthly_increment,
        invoice?.order?.status,
        invoice?.order?.order_type,
        getOrderTypeLabel(invoice?.order?.order_type),
        invoice?.date,
        invoice?.created_at,
        formatDateTime12Hour(invoice?.created_at),
        invoice?.order?.customer?.name,
        invoice?.order?.customer?.phone,
        invoice?.order?.customer?.phone2,
        invoice?.order?.customer?.phone3,
        invoice?.order?.customer?.email,
        invoice?.order?.customer?.address,
        invoice?.order?.customer?.address2,
        invoice?.order?.customer?.address3,
        invoice?.user?.name,
        invoice?.user?.username,
        invoice?.user?.email,
        invoice?.user?.phone,
        invoice?.user?.phone2,
        invoice?.user?.phone3,
        invoice?.user?.address,
        invoice?.user?.address2,
        invoice?.user?.address3,
        invoice?.sub_total,
        invoice?.service,
        invoice?.service_ratio,
        invoice?.delivery_service,
        invoice?.delivery_service_ratio,
        invoice?.tax,
        invoice?.tax_ratio,
        invoice?.discount,
        invoice?.discount_ratio,
        invoice?.total_amount,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");

      return haystack.includes(q);
    });
  }, [
    invoices,
    searchText,
    dateFrom,
    timeFrom,
    dateTo,
    timeTo,
    orderTypeFilter,
    userNameFilter,
  ]);

  const totalSales = filteredInvoices
    .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0)
    .toFixed(2);

  const averageInvoice = filteredInvoices.length
    ? (
        filteredInvoices.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0) /
        filteredInvoices.length
      ).toFixed(2)
    : "0.00";

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <ReceiptIcon sx={{ fontSize: 40, color: "primary.main" }} />
        <Typography variant="h4" sx={{ fontWeight: "bold" }}>
          الفواتير
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          placeholder="ابحث في بيانات الفواتير (رقم الطلب، المستخدم، الحالة، النوع، القيم...)"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 280, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <TextField
          size="small"
          label="من تاريخ"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />

        <TextField
          size="small"
          label="من وقت"
          type="time"
          value={timeFrom}
          onChange={(e) => setTimeFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ width: 120 }}
        />


        <TextField
          size="small"
          label="إلى تاريخ"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />

        <TextField
          size="small"
          label="إلى وقت"
          type="time"
          value={timeTo}
          onChange={(e) => setTimeTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 60 }}
          sx={{ width: 120 }}
        />


        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel id="order-type-filter-label">نوع الطلب</InputLabel>
          <Select
            labelId="order-type-filter-label"
            value={orderTypeFilter}
            label="نوع الطلب"
            onChange={(e) => setOrderTypeFilter(e.target.value)}
          >
            <MenuItem value="all">كل الأنواع</MenuItem>
            <MenuItem value="tables">طاولات</MenuItem>
            <MenuItem value="delivery">توصيل</MenuItem>
            <MenuItem value="takeaway">استلام</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="user-name-filter-label">اسم المستخدم</InputLabel>
          <Select
            labelId="user-name-filter-label"
            value={userNameFilter}
            label="اسم المستخدم"
            onChange={(e) => setUserNameFilter(e.target.value)}
          >
            <MenuItem value="all">كل المستخدمين</MenuItem>
            {userNameOptions.map((userName) => (
              <MenuItem key={userName} value={userName}>
                {userName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
      
        <ExportInvoicesExcelButton
          invoices={filteredInvoices}
          getOrderTypeLabel={getOrderTypeLabel}
        />
        {hasPermission("print") && (
        <Tooltip title="طباعة البيانات بعد الفلاتر" arrow>
          <span>
            <IconButton
              color="primary"
              onClick={handlePrintFilteredInvoices}
              disabled={!filteredInvoices.length}
              sx={{ border: "1px solid", borderColor: "divider" }}
            >
              <PrintIcon />
            </IconButton>
          </span>
        </Tooltip>
        )}
      </Box>

      {/* Summary Cards */}
      {invoices.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  عدد الفواتير
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {filteredInvoices.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  إجمالي المبيعات
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {totalSales}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: "white", mb: 1 }}>
                  متوسط قيمة الفاتورة
                </Typography>
                <Typography variant="h3" sx={{ color: "white", fontWeight: "bold" }}>
                  {averageInvoice}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Invoices Table */}
      {invoices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <ReceiptIcon sx={{ fontSize: 80, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            لا توجد فواتير
          </Typography>
        </Paper>
      ) : filteredInvoices.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            لا توجد نتائج مطابقة للبحث
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
          <Table dir="rtl">
            <TableHead>
              <TableRow sx={{ backgroundColor: "primary.main" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  رقم الفاتورة
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  العميل
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  المستخدم
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  نوع الطلب
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الحالة
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  التاريخ
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الإجمالي الفرعي
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الخدمة
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  التوصيل
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الضريبة
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الخصم
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "right" }}>
                  الإجمالي النهائي
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "center" }}>
                  إجراءات
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInvoices.map((invoice, index) => (
                <TableRow
                  key={invoice.id}
                  onClick={() => handleRowClick(invoice.id)}
                  sx={{
                    "&:hover": { backgroundColor: "action.hover", cursor: "pointer" },
                    backgroundColor: index % 2 === 0 ? "background.default" : "background.paper",
                  }}
                >
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                      <Chip
                        label={index + 1}
                        size="small"
                        variant="outlined"
                        sx={{ minWidth: 36, fontWeight: "bold" }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        #{invoice.order?.general_increment}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {invoice.order?.customer?.name || "غير متوفر"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {invoice.order?.customer?.phone || invoice.order?.customer?.phone2 || ""}
                      </Typography>

                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {invoice.user?.name || "غير متوفر"}
                      </Typography>

                      {invoice.user?.username && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          @{invoice.user?.username}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Chip
                      label={getOrderTypeLabel(invoice.order?.order_type)}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    <Chip
                      label={invoice.order?.status || "غير معروف"}
                      size="small"
                      color={getStatusColor(invoice.order?.status)}
                    />
                  </TableCell>
                  <TableCell sx={{ textAlign: "right" }}>
                    {formatDateTime12Hour(invoice.created_at)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
                    {parseFloat(invoice.sub_total).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", color: "success.main" }}>
                    {parseFloat(invoice.service).toFixed(2)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      ({invoice.service_ratio}%)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", color: "success.main" }}>
                    {parseFloat(invoice.delivery_service).toFixed(2)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      ({invoice.delivery_service_ratio}%)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", color: "success.main" }}>
                    {parseFloat(invoice.tax).toFixed(2)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      ({invoice.tax_ratio}%)
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "right", color: "error.main" }}>
                    -{parseFloat(invoice.discount).toFixed(2)}
                    <Typography variant="caption" color="text.secondary" display="block">
                      ({invoice.discount_ratio}%)
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: "right",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      color: "primary.main",
                    }}
                  >
                    {parseFloat(invoice.total_amount).toFixed(2)}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                    {hasPermission("edit") && (
                      <Tooltip title="تعديل الفاتورة" arrow>
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={(e) => handleEditClick(e, invoice)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "warning.light",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}
                      {hasPermission("delete") && (
                      <Tooltip title="حذف الفاتورة" arrow>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => handleDeleteClick(e, invoice)}
                          sx={{
                            "&:hover": {
                              backgroundColor: "error.light",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>  
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Invoice Details Dialog */}
      <InvoiceDetailsDialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        invoiceId={selectedInvoiceId}
      />

      {/* Edit Invoice Dialog */}
      {selectedInvoice && (
        <EditeInvoiceData
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          invoice={selectedInvoice}
          onUpdated={() => {
            fetchInvoices();
            setOpenEditDialog(false);
          }}
        />
      )}

      {/* Delete Invoice Dialog */}
      {invoiceToDelete && (
        <DeleteInvoiceDialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          invoice={invoiceToDelete}
          onDeleted={() => {
            fetchInvoices();
            setOpenDeleteDialog(false);
          }}
        />
      )}
    </Box>
  );
};

export default InvoicesPage;
